/**
 * Collections ↔ Products toggle.
 * - /collections (list): Collections default, Products fetched into inner panel.
 * - /collections/all: Products default, native product grid + inline collections grid.
 */
(function () {
	const VIEW_COLLECTIONS = 'collections';
	const VIEW_PRODUCTS = 'products';

	let config = null;
	let sectionRoot = null;
	let collectionsContent = null;
	let productsContent = null;
	let productsInner = null;
	let toggleNav = null;
	let fetchAbort = null;
	let fetchGeneration = 0;
	let productsFetchPromise = null;
	let productsViewBusy = false;
	let htmlCache = new Map();
	let nativePushState = null;
	let nativeReplaceState = null;
	let allPageHistoryPatched = false;
	let savedCollectionsFacetsHtml = '';

	function isListPageMode() {
		return Boolean(config && config.enableProductsToggle);
	}

	function isAllPageMode() {
		return Boolean(config && config.enableCollectionsProductsToggle);
	}

	function isSearchPageMode() {
		return Boolean(config && config.isSearchPage);
	}

	function getDefaultView() {
		if (config && config.defaultView === VIEW_COLLECTIONS) return VIEW_COLLECTIONS;
		if (isAllPageMode()) return VIEW_PRODUCTS;
		return VIEW_COLLECTIONS;
	}

	function getSectionId() {
		return config && config.sectionId != null ? String(config.sectionId) : '';
	}

	function getViewFromUrl(url) {
		const view = new URL(url || window.location.href).searchParams.get('view');
		if (view === VIEW_PRODUCTS) return VIEW_PRODUCTS;
		if (view === VIEW_COLLECTIONS) return VIEW_COLLECTIONS;
		return getDefaultView();
	}

	function dedupeFilterSearchParams(input) {
		const params = new URLSearchParams();
		const seen = new Set();

		for (const key of input.keys()) {
			if (key === 'view' || key === 'page' || key === 'phcursor') continue;
			if (!key.startsWith('filter.') && key !== 'sort_by') continue;
			for (const val of input.getAll(key)) {
				const trimmed = String(val).trim();
				if (!trimmed) continue;
				const token = `${key}\0${trimmed}`;
				if (seen.has(token)) continue;
				seen.add(token);
				params.append(key, trimmed);
			}
		}

		const sortBy = input.get('sort_by');
		if (sortBy) params.set('sort_by', sortBy);

		return params;
	}

	function getFilterParamsFromUrl() {
		return dedupeFilterSearchParams(new URL(window.location.href).searchParams);
	}

	function getCanonicalFilterParamsFromUrl() {
		return getFilterParamsFromUrl();
	}

	function mergeFilterSearchParams(...sources) {
		const merged = new URLSearchParams();
		const seen = new Set();

		for (const source of sources) {
			if (!source) continue;
			const params =
				source instanceof URLSearchParams ? source : new URLSearchParams(source || '');
			for (const key of params.keys()) {
				if (!key.startsWith('filter.')) continue;
				for (const val of params.getAll(key)) {
					const trimmed = String(val).trim();
					if (!trimmed) continue;
					const token = `${key}\0${trimmed}`;
					if (seen.has(token)) continue;
					seen.add(token);
					merged.append(key, trimmed);
				}
			}
		}

		for (const source of sources) {
			if (!source) continue;
			const params =
				source instanceof URLSearchParams ? source : new URLSearchParams(source || '');
			const sortBy = params.get('sort_by');
			if (sortBy) {
				merged.set('sort_by', sortBy);
				break;
			}
		}

		return dedupeFilterSearchParams(merged);
	}

	function mergeFilterParamsForViewSwitch(fromView) {
		const urlParams = getCanonicalFilterParamsFromUrl();

		if (fromView === VIEW_COLLECTIONS) {
			const sidebar = getFilterParamsFromSidebar();
			const merged = mergeFilterSearchParams(urlParams, sidebar);
			const sortFromUrl = urlParams.get('sort_by');
			if (sortFromUrl) {
				merged.set('sort_by', sortFromUrl);
			} else if (sidebar.get('sort_by')) {
				merged.set('sort_by', sidebar.get('sort_by'));
			}
			return dedupeFilterSearchParams(merged);
		}

		if (fromView === VIEW_PRODUCTS) {
			const native = getFilterParamsFromNativeForm();
			const sources = [urlParams, native];
			if (
				isSearchPageMode() &&
				typeof window.FacetFiltersForm !== 'undefined' &&
				window.FacetFiltersForm.searchParamsPrev
			) {
				const prev = new URLSearchParams(window.FacetFiltersForm.searchParamsPrev);
				const filterOnly = new URLSearchParams();
				for (const key of prev.keys()) {
					if (!key.startsWith('filter.')) continue;
					for (const val of prev.getAll(key)) {
						filterOnly.append(key, val);
					}
				}
				sources.push(filterOnly);
			}
			return mergeFilterSearchParams(...sources);
		}

		return urlParams;
	}

	function writeCanonicalFilterUrl(view, params) {
		const next = buildCollectionsPageUrl(view, params);
		const current = window.location.pathname + window.location.search;
		if (next === current) return;
		const replace = nativeReplaceState || history.replaceState.bind(history);
		replace({ clistView: view, clistFilterSync: true }, '', next);
	}

	function getFilterParamsFromSidebar() {
		const params = new URLSearchParams();
		const seen = new Set();
		const root = sectionRoot || document;

		root.querySelectorAll('.clist-facet-checkbox:checked').forEach((cb) => {
			const param = cb.getAttribute('data-storefront-param');
			const val = String(cb.value || '').trim();
			if (!param || !val) return;
			const token = `${param}\0${val}`;
			if (seen.has(token)) return;
			seen.add(token);
			params.append(param, val);
		});

		const sortSelect =
			root.querySelector('[data-clist-products-sort]:not([disabled])') ||
			root.querySelector('[data-clist-collections-sort]:not([disabled])') ||
			root.querySelector('.js-clist-sort-select:not([disabled]):not([data-clist-products-sort])');
		if (sortSelect && sortSelect.value) {
			params.set('sort_by', sortSelect.value);
		}

		return params;
	}

	function paramsHaveFilters(params) {
		return [...params.keys()].some((key) => key.startsWith('filter.'));
	}

	function getActiveFilterParams() {
		if (isAllPageMode() && isAllPageProductsVisible()) {
			return getCanonicalFilterParamsFromUrl();
		}
		const sidebar = getFilterParamsFromSidebar();
		const urlParams = getFilterParamsFromUrl();

		// Sidebar always includes sort_by when the sort select has a value — that must
		// not block reading filter.* params from the URL on cold load / deep links.
		if (paramsHaveFilters(sidebar)) {
			return sidebar;
		}
		if (paramsHaveFilters(urlParams)) {
			const merged = new URLSearchParams(urlParams);
			if (sidebar.has('sort_by')) {
				merged.set('sort_by', sidebar.get('sort_by'));
			}
			return merged;
		}
		if ([...sidebar.keys()].length > 0) return sidebar;
		return urlParams;
	}

	function preserveSearchContext(url) {
		try {
			const current = new URL(window.location.href);
			const q = current.searchParams.get('q');
			if (q) {
				url.searchParams.set('q', q);
				const prefix = current.searchParams.get('options[prefix]');
				if (prefix) url.searchParams.set('options[prefix]', prefix);
			}
		} catch (e) {
			/* ignore */
		}
		return url;
	}

	function appendSearchContextToParams(input) {
		const params =
			input instanceof URLSearchParams ? input : new URLSearchParams(input || '');
		try {
			const current = new URL(window.location.href);
			const q = current.searchParams.get('q');
			if (q && !params.has('q')) {
				params.set('q', q);
				const prefix = current.searchParams.get('options[prefix]');
				if (prefix) params.set('options[prefix]', prefix);
			}
		} catch (e) {
			/* ignore */
		}
		return params;
	}

	function buildCollectionsPageUrl(view, filterParams) {
		const url = new URL(window.location.pathname, window.location.origin);
		preserveSearchContext(url);
		const params = dedupeFilterSearchParams(filterParams || getActiveFilterParams());
		for (const key of params.keys()) {
			for (const val of params.getAll(key)) {
				url.searchParams.append(key, val);
			}
		}
		if (view === VIEW_PRODUCTS) {
			url.searchParams.set('view', VIEW_PRODUCTS);
		} else if (view === VIEW_COLLECTIONS) {
			url.searchParams.set('view', VIEW_COLLECTIONS);
		}
		return url.pathname + url.search;
	}

	function getProductsFetchPath() {
		return (config && config.productsFetchPath) || '/collections/all';
	}

	function parseProductsSection(html) {
		const doc = new DOMParser().parseFromString(html, 'text/html');
		const section = doc.querySelector('[id^="shopify-section-"]');
		if (!section) return null;

		const facetForm = section.querySelector('facet-filters-form');
		const grid = section.querySelector('#ProductGridContainer');
		const productGrid = section.querySelector('#product-grid');

		return {
			facetForm: facetForm,
			gridHtml: grid ? grid.outerHTML : '',
			productsSectionId: (productGrid && productGrid.dataset.id) || '',
		};
	}

	function setFacetsMode(mode) {
		const sid = getSectionId();
		const collectionsMount = sectionRoot?.querySelector(`#CollectionListFacets-${sid}`);
		const productsHost = sectionRoot?.querySelector(`#ClistProductsFacetHost-${sid}`);
		const collectionPills = sectionRoot?.querySelectorAll(
			'.collection-list-active-facets, .collection-list-active-facets-mobile'
		);
		const collectionsPerRow = sectionRoot?.querySelector(
			'.per-row__wrapper[data-clist-per-row="collections"]:not(.per-row__wrapper-mobile)'
		);

		if (mode === VIEW_PRODUCTS) {
			if (productsHost) productsHost.hidden = true;
			if (collectionsMount) collectionsMount.hidden = false;
			collectionPills?.forEach((el) => {
				el.hidden = false;
			});
		} else {
			if (productsHost) productsHost.hidden = true;
			if (collectionsMount) collectionsMount.hidden = false;
			collectionPills?.forEach((el) => {
				el.hidden = false;
			});
		}

		if (collectionsPerRow) {
			collectionsPerRow.style.display = '';
		}
	}

	function getToolbarPerRowValue() {
		const activeBtn = sectionRoot?.querySelector(
			'.per-row__wrapper[data-clist-per-row="collections"] .per-row__button.active'
		);
		return activeBtn?.getAttribute('data-per-row') || 'large';
	}

	function syncPerRowToActiveGrid() {
		if (!sectionRoot) return;
		const value = getToolbarPerRowValue();
		if (window.ClistPerRow && typeof window.ClistPerRow.applyPerRowValue === 'function') {
			window.ClistPerRow.applyPerRowValue(sectionRoot, value);
			if (typeof window.ClistPerRow.setActivePerRowButtons === 'function') {
				window.ClistPerRow.setActivePerRowButtons(sectionRoot, value);
			}
			scheduleToolbarAlignMode();
			return;
		}

		const collectionsGrid = sectionRoot.querySelector('#CollectionListGridContainer');
		const productsGrid = sectionRoot.querySelector(
			'[data-clist-products-inner] #ProductGridContainer'
		);

		if (productsGrid) productsGrid.setAttribute('data-productis-in-row', value);
		if (collectionsGrid) collectionsGrid.setAttribute('data-productis-in-row', value);
		scheduleToolbarAlignMode();
	}

	function mountNativeFacets(facetForm) {
		const sid = getSectionId();
		const host = sectionRoot?.querySelector(`#ClistProductsFacetHost-${sid}`);
		const collectionsMount = sectionRoot?.querySelector(`#CollectionListFacets-${sid}`);
		if (!host || !facetForm) return;

		if (!savedCollectionsFacetsHtml && collectionsMount) {
			savedCollectionsFacetsHtml = collectionsMount.innerHTML;
		}

		const imported = document.importNode(facetForm, true);
		imported.querySelectorAll('.facets__top').forEach((el) => el.remove());
		host.replaceChildren();
		host.appendChild(imported);
		setFacetsMode(VIEW_PRODUCTS);

		if (window.ClistProductsPlp && typeof window.ClistProductsPlp.patchFacetFiltersForm === 'function') {
			window.ClistProductsPlp.patchFacetFiltersForm();
		}

		const nativeForm = host.querySelector('facet-filters-form');
		if (nativeForm && typeof nativeForm.bindFacetTriggers === 'function') {
			nativeForm.bindFacetTriggers();
		}
	}

	function cleanupOrphanedQuickAddModals() {
		if (typeof window.ClistProductsPlp?.cleanupOrphanedQuickAddModals === 'function') {
			window.ClistProductsPlp.cleanupOrphanedQuickAddModals();
		}
	}

	function injectProductsGrid(gridHtml) {
		if (!productsInner || !gridHtml) return;
		cleanupOrphanedQuickAddModals();
		productsInner.innerHTML = gridHtml;
		productsInner.hidden = false;
		productsInner.classList.add('clist-products-plp-grid');

		if (window.ClistProductsPlp) {
			if (typeof window.ClistProductsPlp.patchFacetFiltersForm === 'function') {
				window.ClistProductsPlp.patchFacetFiltersForm();
			}
			if (typeof window.ClistProductsPlp.wireProductsLoadMore === 'function') {
				window.ClistProductsPlp.wireProductsLoadMore();
			}
		}

		if (typeof colorSwatches === 'function') colorSwatches();
		window.dispatchEvent(new Event('resize'));

		document.dispatchEvent(
			new CustomEvent('clist:products-injected', {
				detail: { sectionId: getSectionId() },
			})
		);

		if (
			window.ClistProductsPlp &&
			typeof window.ClistProductsPlp.applyAuthoritativeCountFromHtml === 'function'
		) {
			window.ClistProductsPlp.applyAuthoritativeCountFromHtml();
		} else if (
			window.ClistProductsPlp &&
			typeof window.ClistProductsPlp.syncProductsToolbarCountFromDom === 'function'
		) {
			window.ClistProductsPlp.syncProductsToolbarCountFromDom();
		}

	}

	function installHistoryPatch() {
		if (nativePushState) return;
		nativePushState = history.pushState.bind(history);
		nativeReplaceState = history.replaceState.bind(history);

		history.pushState = (state, title, url) => {
			if (!window.__clistProductsViewActive || url == null) {
				return nativePushState(state, title, url);
			}
			const raw = typeof url === 'string' && url.includes('?') ? url.slice(url.indexOf('?') + 1) : '';
			const filterParams = dedupeFilterSearchParams(new URLSearchParams(raw));
			const nextUrl = buildCollectionsPageUrl(VIEW_PRODUCTS, filterParams);
			return nativePushState(
				{ ...(state && typeof state === 'object' ? state : {}), clistView: VIEW_PRODUCTS },
				title,
				nextUrl
			);
		};

		history.replaceState = (state, title, url) => {
			if (!window.__clistProductsViewActive || url == null) {
				return nativeReplaceState(state, title, url);
			}
			const raw = typeof url === 'string' && url.includes('?') ? url.slice(url.indexOf('?') + 1) : '';
			const filterParams = dedupeFilterSearchParams(new URLSearchParams(raw));
			const nextUrl = buildCollectionsPageUrl(VIEW_PRODUCTS, filterParams);
			return nativeReplaceState(
				{ ...(state && typeof state === 'object' ? state : {}), clistView: VIEW_PRODUCTS },
				title,
				nextUrl
			);
		};
	}

	window.__clistUpdateProductsUrl = function (searchParams) {
		const filterParams = dedupeFilterSearchParams(new URLSearchParams(searchParams || ''));
		const nextUrl = buildCollectionsPageUrl(VIEW_PRODUCTS, filterParams);
		const push = nativePushState || history.pushState.bind(history);
		push({ clistView: VIEW_PRODUCTS, searchParams: filterParams.toString() }, '', nextUrl);
	};

	window.__clistInvalidateProductsCache = function (cacheKey) {
		if (cacheKey) {
			htmlCache.delete(cacheKey);
		} else {
			htmlCache.clear();
		}
	};

	function setToggleActive(view) {
		if (!sectionRoot) return;
		sectionRoot.querySelectorAll('[data-collections-products-toggle] [data-clist-view]').forEach((btn) => {
			const isActive = btn.getAttribute('data-clist-view') === view;
			btn.classList.toggle('is-active', isActive);
			btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
		});
	}

	function isProductsPanelVisible() {
		return Boolean(productsContent && !productsContent.hidden);
	}

	function setProductsLoading(loading) {
		const loadingEl = productsContent?.querySelector('[data-clist-products-loading]');
		const errorEl = productsContent?.querySelector('[data-clist-products-error]');
		if (loadingEl) loadingEl.hidden = !loading;
		if (errorEl) errorEl.hidden = true;
		if (productsInner) productsInner.hidden = loading;
	}

	function setProductsError(message) {
		const loadingEl = productsContent?.querySelector('[data-clist-products-loading]');
		const errorEl = productsContent?.querySelector('[data-clist-products-error]');
		const msgEl = productsContent?.querySelector('[data-clist-products-error-message]');
		if (loadingEl) loadingEl.hidden = true;
		if (productsInner) productsInner.hidden = true;
		if (errorEl) errorEl.hidden = false;
		if (msgEl) msgEl.textContent = message || 'Could not load products. Please try again.';
	}

	async function fetchProductsSection(options = {}) {
		const cacheKey = getActiveFilterParams().toString();
		if (!options.forceRefetch && htmlCache.has(cacheKey)) {
			const cached = htmlCache.get(cacheKey);
			const generation = window.ClistProductsPlp?.beginFilterRenderRequest?.();
			if (generation == null) return cached;
			return { ...cached, generation };
		}

		const generation =
			window.ClistProductsPlp?.beginFilterRenderRequest?.() ?? ++fetchGeneration;
		if (fetchAbort) fetchAbort.abort();
		fetchAbort = new AbortController();

		let html;
		if (
			window.ClistProductsPlp &&
			typeof window.ClistProductsPlp.fetchProductsSectionHtml === 'function'
		) {
			const result = await window.ClistProductsPlp.fetchProductsSectionHtml(cacheKey, {
				generation,
			});
			html = result.html;
			if (result.sectionId) config.productsSectionId = result.sectionId;
		} else {
			const url =
				window.ClistProductsPlp &&
				typeof window.ClistProductsPlp.buildProductsSectionUrl === 'function'
					? window.ClistProductsPlp.buildProductsSectionUrl(cacheKey)
					: (() => {
							const u = new URL(getProductsFetchPath(), window.location.origin);
							const params = new URLSearchParams(cacheKey);
							for (const key of params.keys()) {
								for (const val of params.getAll(key)) {
									u.searchParams.append(key, val);
								}
							}
							u.searchParams.set(
								'section_id',
								config.productsSectionKey || 'main-collection-product-grid'
							);
							return u.toString();
					  })();

			const res = await fetch(url, { credentials: 'same-origin', signal: fetchAbort.signal });
			if (!res.ok) throw new Error(`HTTP ${res.status}`);
			html = await res.text();
		}
		const parts = parseProductsSection(html);
		if (!parts || !parts.gridHtml) throw new Error('Product section markup missing');

		if (window.ClistProductsPlp?.isCurrentFilterGeneration) {
			if (!window.ClistProductsPlp.isCurrentFilterGeneration(generation)) {
				throw new DOMException('Aborted', 'AbortError');
			}
		} else if (generation !== fetchGeneration) {
			throw new DOMException('Aborted', 'AbortError');
		}

		config.productsSectionId = parts.productsSectionId;
		parts.rawHtml = html;
		parts.generation = generation;
		htmlCache.set(cacheKey, parts);
		return parts;
	}

	function applyProductsSection(parts) {
		setFacetsMode(VIEW_PRODUCTS);
		injectProductsGrid(parts.gridHtml);
		syncPerRowToActiveGrid();
		if (parts.rawHtml && window.ClistProductsPlp?.applyAuthoritativeCountFromHtml) {
			window.ClistProductsPlp.applyAuthoritativeCountFromHtml(parts.rawHtml, parts.generation);
		} else if (parts.rawHtml && window.ClistProductsPlp?.updateToolbarProductCount) {
			window.ClistProductsPlp.updateToolbarProductCount(parts.rawHtml);
		}
		document.dispatchEvent(
			new CustomEvent('clist:products-view-ready', {
				detail: { sectionId: getSectionId() },
			})
		);
	}

	function setCollectionLoadMoreVisible(visible) {
		const wrap = sectionRoot?.querySelector(
			'[data-clist-collections-column] .collection-list-load-more-wrapper'
		);
		const btn = sectionRoot?.querySelector(
			'[data-clist-collections-column] .js-clist-load-more'
		);
		if (wrap) wrap.hidden = !visible;
		if (btn) btn.hidden = !visible;
	}

	function applyCollectionsFiltersFromUrl() {
		document.dispatchEvent(
			new CustomEvent('collection-list:apply-url', {
				detail: { sectionId: getSectionId() },
			})
		);
	}

	function showCollectionsView(options = {}) {
		window.__clistProductsViewActive = false;
		setFacetsMode(VIEW_COLLECTIONS);

		sectionRoot?.classList.remove('clist-view-products-active');
		sectionRoot?.classList.add('clist-view-collections-active');
		sectionRoot
			?.querySelector('#CollectionListGridContainer')
			?.classList.remove('clist-main-column--products-active');
		if (collectionsContent) collectionsContent.hidden = false;
		if (productsContent) {
			productsContent.hidden = true;
			productsContent.querySelector('[data-clist-products-loading]')?.setAttribute('hidden', '');
			productsContent.querySelector('[data-clist-products-error]')?.setAttribute('hidden', '');
		}

		setCollectionLoadMoreVisible(true);
		setToggleActive(VIEW_COLLECTIONS);
		syncSharedToolbar(VIEW_COLLECTIONS);

		if (!options.skipUrl) {
			const next = buildCollectionsPageUrl(VIEW_COLLECTIONS, getActiveFilterParams());
			const push = nativePushState || history.pushState.bind(history);
			if (next !== window.location.pathname + window.location.search) {
				push({ clistView: VIEW_COLLECTIONS }, '', next);
			}
		}

		syncPerRowToActiveGrid();
		applyCollectionsFiltersFromUrl();
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				scheduleToolbarAlignMode(VIEW_COLLECTIONS);
			});
		});
	}

	function activateProductsViewUi(options = {}) {
		sectionRoot?.classList.add('clist-view-products-active');
		sectionRoot?.classList.remove('clist-view-collections-active');
		if (collectionsContent) collectionsContent.hidden = true;
		if (productsContent) productsContent.hidden = false;
		sectionRoot
			?.querySelector('#CollectionListGridContainer')
			?.classList.add('clist-main-column--products-active');
		setCollectionLoadMoreVisible(false);
		setToggleActive(VIEW_PRODUCTS);
		syncSharedToolbar(VIEW_PRODUCTS);

		window.__clistProductsViewActive = true;
		installHistoryPatch();

		if (!options.skipUrl) {
			const next = buildCollectionsPageUrl(VIEW_PRODUCTS, getActiveFilterParams());
			const push = nativePushState || history.pushState.bind(history);
			if (next !== window.location.pathname + window.location.search) {
				push({ clistView: VIEW_PRODUCTS }, '', next);
			}
		}
	}

	async function showProductsView(options = {}) {
		if (productsViewBusy && !options.forceRefetch) return;
		productsViewBusy = true;

		activateProductsViewUi(options);

		try {
			document.dispatchEvent(
				new CustomEvent('clist:products-view-ready', {
					detail: { sectionId: getSectionId(), syncOnly: true },
				})
			);

			const cacheKey = getActiveFilterParams().toString();
			if (!options.forceRefetch && htmlCache.has(cacheKey)) {
				const cached = htmlCache.get(cacheKey);
				const generation = window.ClistProductsPlp?.beginFilterRenderRequest?.();
				applyProductsSection(
					generation == null ? cached : { ...cached, generation }
				);
				setProductsLoading(false);
				return;
			}

			if (productsFetchPromise && !options.forceRefetch) {
				setProductsLoading(true);
				const parts = await productsFetchPromise;
				if (!window.__clistProductsViewActive) return;
				applyProductsSection(parts);
				setProductsLoading(false);
				return;
			}

			setProductsLoading(true);
			productsFetchPromise = fetchProductsSection(options);
			const parts = await productsFetchPromise;
			productsFetchPromise = null;

			if (!window.__clistProductsViewActive) return;
			applyProductsSection(parts);
			setProductsLoading(false);
		} catch (e) {
			if (e && e.name === 'AbortError') return;
			console.error('[collections-products-toggle]', e);
			setProductsError(e && e.message ? e.message : undefined);
		} finally {
			productsViewBusy = false;
		}
	}

	function onPopstate() {
		const view = getViewFromUrl();
		if (view === VIEW_PRODUCTS) {
			showProductsView({ skipUrl: true, forceRefetch: true });
		} else {
			showCollectionsView({ skipUrl: true });
		}
	}

	function wireToggle() {
		if (!sectionRoot) return;

		sectionRoot.addEventListener('click', (e) => {
			const btn = e.target.closest('[data-collections-products-toggle] [data-clist-view]');
			if (!btn || btn.disabled) return;
			const view = btn.getAttribute('data-clist-view');
			const productsVisible = isProductsPanelVisible();

			if (view === VIEW_PRODUCTS && !productsVisible) {
				sectionRoot.querySelectorAll('[data-collections-products-toggle] [data-clist-view]').forEach((b) => {
					b.disabled = true;
				});
				Promise.resolve(showProductsView()).finally(() => {
					sectionRoot.querySelectorAll('[data-collections-products-toggle] [data-clist-view]').forEach((b) => {
						b.disabled = false;
					});
				});
			} else if (view === VIEW_COLLECTIONS && productsVisible) {
				showCollectionsView();
			}
		});

		const retryBtn = productsContent?.querySelector('[data-clist-products-retry]');
		if (retryBtn) {
			retryBtn.addEventListener('click', () => {
				htmlCache.delete(getActiveFilterParams().toString());
				showProductsView({ skipUrl: true, forceRefetch: true });
			});
		}
	}

	function isAllPageProductsVisible() {
		if (!sectionRoot) return true;
		const productGrid = sectionRoot.querySelector('#ProductGridContainer');
		return productGrid ? !productGrid.hidden : true;
	}

	function getSharedToolbar() {
		if (!sectionRoot) return null;
		return (
			sectionRoot.querySelector('[data-clist-shared-toolbar]') ||
			sectionRoot.querySelector('.clist-toolbar-grid')
		);
	}

	function getActiveToolbarView() {
		if (!sectionRoot) return getDefaultView();
		if (sectionRoot.classList.contains('clist-view-collections-active')) return VIEW_COLLECTIONS;
		if (sectionRoot.classList.contains('clist-view-products-active')) return VIEW_PRODUCTS;
		return window.__clistProductsViewActive ? VIEW_PRODUCTS : VIEW_COLLECTIONS;
	}

	function applyToolbarAlignMode(view) {
		const toolbar = getSharedToolbar();
		if (!toolbar) return;
		const mode = view === VIEW_COLLECTIONS ? 'collections' : 'products';
		toolbar.querySelectorAll('.clist-toolbar-track, .clist-toolbar-main').forEach((el) => {
			el.setAttribute('data-clist-toolbar-align', mode);
		});
	}

	/** Defer until after tab panels are visible and the grid has been laid out. */
	function scheduleToolbarAlignMode(view) {
		const resolved = view || getActiveToolbarView();
		applyToolbarAlignMode(resolved);
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				applyToolbarAlignMode(resolved);
				if (window.ClistToolbarAlign && typeof window.ClistToolbarAlign.schedule === 'function') {
					window.ClistToolbarAlign.schedule();
				}
			});
		});
	}

	function syncSharedToolbar(view) {
		if (!sectionRoot) return;
		const toolbar = getSharedToolbar();
		if (!toolbar) return;
		const isProducts = view === VIEW_PRODUCTS;
		toolbar.querySelectorAll('[data-clist-toolbar-part]').forEach((el) => {
			const part = el.getAttribute('data-clist-toolbar-part');
			el.hidden = part === 'products' ? !isProducts : isProducts;
		});

		const productsSort = sectionRoot.querySelector('[data-clist-products-sort]');
		const collectionsSort = sectionRoot.querySelector('[data-clist-collections-sort]');
		if (productsSort) productsSort.disabled = !isProducts;
		if (collectionsSort) collectionsSort.disabled = isProducts;

		sectionRoot
			.querySelectorAll('.js-clist-sort-select:not([data-clist-products-sort])')
			.forEach((el) => {
				el.disabled = isProducts;
			});

		const sortFromUrl = new URLSearchParams(window.location.search).get('sort_by');
		if (sortFromUrl) {
			if (productsSort && isProducts) productsSort.value = sortFromUrl;
			if (collectionsSort && !isProducts) collectionsSort.value = sortFromUrl;
		}

		scheduleToolbarAlignMode(view);
	}

	function isProductsTabActive() {
		if (isAllPageMode()) return isAllPageProductsVisible();
		return Boolean(window.__clistProductsViewActive);
	}

	function findProductsFacetForm() {
		const facetEl =
			sectionRoot?.querySelector('#ClistProductsFacetHost facet-filters-form') ||
			sectionRoot?.querySelector('[data-clist-products-facets] facet-filters-form');
		const form =
			(facetEl && facetEl.querySelector('#FacetFiltersForm')) ||
			sectionRoot?.querySelector('#FacetFiltersForm');
		return { facetEl, form };
	}

	let productsSortBusy = false;

	function submitProductsToolbarSort(select, event) {
		if (!sectionRoot || !select || productsSortBusy) return;

		const { facetEl, form } = findProductsFacetForm();
		const FacetCtor = customElements.get('facet-filters-form');

		if (isSearchPageMode() && isProductsTabActive()) {
			productsSortBusy = true;
			document.querySelectorAll('.no-js-list').forEach((el) => el.remove());

			const filterParams = appendSearchContextToParams(
				dedupeFilterSearchParams(
					facetEl && form
						? new URLSearchParams(facetEl.createSearchParams(form))
						: getActiveFilterParams()
				)
			);
			if (select.value) filterParams.set('sort_by', select.value);

			if (
				typeof window.__clistSortSearchProducts === 'function' &&
				window.__clistSortSearchProducts(select.value, {
					filterParams: filterParams.toString(),
				})
			) {
				const nextUrl = buildCollectionsPageUrl(VIEW_PRODUCTS, filterParams);
				const push = nativePushState || history.pushState.bind(history);
				if (nextUrl !== window.location.pathname + window.location.search) {
					push({ clistView: VIEW_PRODUCTS }, '', nextUrl);
				}
				if (typeof window.__clistSyncAllFilterUiFromParams === 'function') {
					window.__clistSyncAllFilterUiFromParams(filterParams);
				}
				productsSortBusy = false;
				return;
			}

			if (typeof window.__clistBeginSearchProductsLoading === 'function') {
				window.__clistBeginSearchProductsLoading({ force: true });
			}

			if (FacetCtor && typeof FacetCtor.renderPage === 'function') {
				const nextUrl = buildCollectionsPageUrl(VIEW_PRODUCTS, filterParams);
				const push = nativePushState || history.pushState.bind(history);
				push({ clistView: VIEW_PRODUCTS }, '', nextUrl);
				Promise.resolve(FacetCtor.renderPage(filterParams.toString(), null, true))
					.then(() => {
						if (typeof window.__clistSyncAllFilterUiFromParams === 'function') {
							window.__clistSyncAllFilterUiFromParams(filterParams);
						}
					})
					.catch(() => {})
					.finally(() => {
						productsSortBusy = false;
					});
			} else {
				renderNativeProductsWithFilters(filterParams, { skipUrl: false });
				productsSortBusy = false;
			}
			return;
		}

		if (isListPageMode() && isProductsTabActive()) {
			productsSortBusy = true;
			document.querySelectorAll('.no-js-list').forEach((el) => el.remove());

			if (facetEl && form && FacetCtor && typeof FacetCtor.renderPage === 'function') {
				const params = new URLSearchParams(facetEl.createSearchParams(form));
				if (select.value) params.set('sort_by', select.value);
				Promise.resolve(FacetCtor.renderPage(params.toString(), event))
					.catch(() => {})
					.finally(() => {
						productsSortBusy = false;
					});
				return;
			}

			const params = getActiveFilterParams();
			if (select.value) params.set('sort_by', select.value);
			htmlCache.delete(params.toString());
			const push = nativePushState || history.pushState.bind(history);
			push({ clistView: VIEW_PRODUCTS }, '', buildCollectionsPageUrl(VIEW_PRODUCTS, params));
			Promise.resolve(showProductsView({ skipUrl: true, forceRefetch: true })).finally(() => {
				productsSortBusy = false;
			});
			return;
		}

		if (!isAllPageMode() || !facetEl || !form) return;

		productsSortBusy = true;
		document.querySelectorAll('.no-js-list').forEach((el) => el.remove());
		const params = new URLSearchParams(facetEl.createSearchParams(form));
		if (select.value) params.set('sort_by', select.value);

		if (FacetCtor && typeof FacetCtor.renderPage === 'function') {
			Promise.resolve(FacetCtor.renderPage(params.toString(), event))
				.catch(() => {})
				.finally(() => {
					productsSortBusy = false;
				});
		} else {
			productsSortBusy = false;
		}
	}

	function bindProductsToolbarSort() {
		if (!sectionRoot || sectionRoot.dataset.clistProductsSortBound === '1') return;
		sectionRoot.dataset.clistProductsSortBound = '1';

		sectionRoot.addEventListener(
			'change',
			(e) => {
				const select = e.target;
				if (!(select instanceof HTMLSelectElement)) return;
				if (!select.matches('[data-clist-products-sort]')) return;
				if (!isProductsTabActive()) return;
				if (select.disabled) return;

				e.stopPropagation();
				e.stopImmediatePropagation();
				submitProductsToolbarSort(select, e);
			},
			true
		);
	}

	function syncAllPagePerRow() {
		if (!sectionRoot) return;
		const value =
			sectionRoot.querySelector(
				'.per-row__wrapper .per-row__button.active, .per-row__wrapper .per-row__button.is-active'
			)?.getAttribute('data-per-row') || 'large';
		if (window.ClistPerRow && typeof window.ClistPerRow.applyPerRowValue === 'function') {
			window.ClistPerRow.applyPerRowValue(sectionRoot, value);
			if (typeof window.ClistPerRow.setActivePerRowButtons === 'function') {
				window.ClistPerRow.setActivePerRowButtons(sectionRoot, value);
			}
			scheduleToolbarAlignMode();
			return;
		}
		const collectionsGrid = sectionRoot.querySelector('[data-clist-collections-grid]');
		const productsGrid = sectionRoot.querySelector('#ProductGridContainer');
		if (collectionsGrid) collectionsGrid.setAttribute('data-productis-in-row', value);
		if (productsGrid) productsGrid.setAttribute('data-productis-in-row', value);
		scheduleToolbarAlignMode();
	}

	function afterAllPageLayout() {
		scheduleToolbarAlignMode();
		if (window.ClistProductsPlp?.syncProductsToolbarCountFromDom) {
			window.ClistProductsPlp.syncProductsToolbarCountFromDom();
		}
		document.dispatchEvent(
			new CustomEvent('clist:products-view-ready', {
				detail: { sectionId: getSectionId(), syncOnly: true },
			})
		);
		document.dispatchEvent(new CustomEvent('clist:sync-search-column-height'));
	}

	function installAllPageHistoryPatch() {
		if (allPageHistoryPatched) return;
		allPageHistoryPatched = true;
		if (!nativePushState) {
			nativePushState = history.pushState.bind(history);
			nativeReplaceState = history.replaceState.bind(history);
		}

		const preserveProductsView = (url) => {
			if (!url || typeof url !== 'string' || !isAllPageMode() || !isAllPageProductsVisible()) return url;
			try {
				const parsed = new URL(url, window.location.origin);
				if (parsed.searchParams.get('view') === VIEW_PRODUCTS) return url;
				parsed.searchParams.set('view', VIEW_PRODUCTS);
				return parsed.pathname + parsed.search + parsed.hash;
			} catch (e) {
				return url;
			}
		};

		history.pushState = (state, title, url) => {
			if (isAllPageMode() && isAllPageProductsVisible() && url != null) {
				return nativePushState(state, title, preserveProductsView(url));
			}
			return nativePushState(state, title, url);
		};

		history.replaceState = (state, title, url) => {
			if (isAllPageMode() && isAllPageProductsVisible() && url != null) {
				return nativeReplaceState(state, title, preserveProductsView(url));
			}
			return nativeReplaceState(state, title, url);
		};
	}

	function getFilterParamsFromNativeForm() {
		const { facetEl, form } = findProductsFacetForm();
		if (!facetEl || !form || typeof facetEl.createSearchParams !== 'function') {
			return new URLSearchParams();
		}
		return dedupeFilterSearchParams(new URLSearchParams(facetEl.createSearchParams(form)));
	}

	function filterParamsEqual(a, b) {
		const left = dedupeFilterSearchParams(
			a instanceof URLSearchParams ? a : new URLSearchParams(a || '')
		);
		const right = dedupeFilterSearchParams(
			b instanceof URLSearchParams ? b : new URLSearchParams(b || '')
		);
		return left.toString() === right.toString();
	}

	function syncNativeFacetsFromParams(params) {
		const { form } = findProductsFacetForm();
		if (!form) return;
		const target = dedupeFilterSearchParams(
			params instanceof URLSearchParams ? params : new URLSearchParams(params || '')
		);
		const normalize =
			window.ClistProductsPlp && typeof window.ClistProductsPlp.normalizeFacetParamValue === 'function'
				? window.ClistProductsPlp.normalizeFacetParamValue.bind(window.ClistProductsPlp)
				: (value) => String(value).trim();
		const valueSelected = (param, checkboxValue) =>
			target.getAll(param).some((urlVal) => normalize(urlVal) === normalize(checkboxValue));
		const forms = [form, document.getElementById('FacetFiltersFormMobile')].filter(Boolean);
		forms.forEach((facetForm) => {
			facetForm.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach((input) => {
				input.checked = valueSelected(input.name, input.value);
			});
		});
	}

	function syncAllFilterUiFromParams(params) {
		const canonical = dedupeFilterSearchParams(
			params instanceof URLSearchParams ? params : new URLSearchParams(params || '')
		);
		syncNativeFacetsFromParams(canonical);
		if (window.ClistProductsPlp?.scheduleSyncProductsSidebarFilterOptions) {
			window.ClistProductsPlp.scheduleSyncProductsSidebarFilterOptions(canonical);
		} else if (window.ClistProductsPlp?.syncProductsSidebarFilterOptions) {
			window.ClistProductsPlp.syncProductsSidebarFilterOptions(canonical);
		} else if (window.ClistProductsPlp?.ensureSelectedProductsFiltersVisible) {
			window.ClistProductsPlp.ensureSelectedProductsFiltersVisible(canonical);
		}
		if (window.ClistProductsPlp?.rebuildNativeActiveFacetsFromForm) {
			window.ClistProductsPlp.rebuildNativeActiveFacetsFromForm(canonical);
		}
		document.dispatchEvent(
			new CustomEvent('clist:sync-all-filters-from-params', {
				detail: { sectionId: getSectionId(), filterParams: canonical.toString() },
			})
		);
		document.dispatchEvent(
			new CustomEvent('clist:sync-collections-filters-from-products-dom', {
				detail: { sectionId: getSectionId() },
			})
		);
		if (window.ClistFilterOrder?.applyCanonicalGroupLabelsInDom) {
			window.ClistFilterOrder.applyCanonicalGroupLabelsInDom(sectionRoot || document);
		}
	}

	function syncSearchNativeFacetsFromUrl() {
		if (!isSearchPageMode()) return;
		syncNativeFacetsFromParams(getCanonicalFilterParamsFromUrl());
	}

	window.__clistSyncSearchNativeFacetsFromUrl = syncSearchNativeFacetsFromUrl;
	window.__clistGetCanonicalFilterParamsFromUrl = getCanonicalFilterParamsFromUrl;
	window.__clistSyncAllFilterUiFromParams = syncAllFilterUiFromParams;

	function renderNativeProductsWithFilters(filterParams, options = {}) {
		// FacetFiltersForm.renderPage() can replace section DOM; refresh refs on each call.
		if (config && config.sectionId != null) {
			const sid = String(config.sectionId);
			const fresh = document.getElementById(`shopify-section-${sid}`);
			if (fresh) sectionRoot = fresh;
		}

		const params = appendSearchContextToParams(
			dedupeFilterSearchParams(filterParams || getActiveFilterParams())
		);
		const nextUrl = buildCollectionsPageUrl(VIEW_PRODUCTS, params);
		const FacetCtor = customElements.get('facet-filters-form');
		if (FacetCtor && typeof FacetCtor.renderPage === 'function') {
			if (!options.skipUrl) {
				const push = nativePushState || history.pushState.bind(history);
				push({ clistView: VIEW_PRODUCTS }, '', nextUrl);
			}
			FacetCtor.renderPage(params.toString(), null, true);
			return;
		}
		if (!options.skipUrl) {
			window.location.assign(nextUrl);
		}
	}

	function showAllPageCollectionsView(options = {}) {
		if (config && config.sectionId != null) {
			const sid = String(config.sectionId);
			const fresh = document.getElementById(`shopify-section-${sid}`);
			if (fresh) sectionRoot = fresh;
		}
		if (!sectionRoot) return;

		const filterParams = dedupeFilterSearchParams(
			options.filterParams
				? new URLSearchParams(options.filterParams)
				: mergeFilterParamsForViewSwitch(VIEW_PRODUCTS)
		);

		writeCanonicalFilterUrl(VIEW_COLLECTIONS, filterParams);

		const productsFacets = sectionRoot.querySelector('[data-clist-products-facets]');
		const collectionsFacets = sectionRoot.querySelector('[data-clist-collections-facets]');
		const collectionsColumn = sectionRoot.querySelector('[data-clist-collections-column]');
		const collectionsGrid = sectionRoot.querySelector('[data-clist-collections-grid]');
		const productGrid = sectionRoot.querySelector('#ProductGridContainer');

		sectionRoot.classList.add('clist-view-collections-active');
		sectionRoot.classList.remove('clist-view-products-active');
		window.__clistProductsViewActive = false;
		if (productsFacets) productsFacets.hidden = true;
		if (collectionsFacets) collectionsFacets.hidden = false;
		if (collectionsColumn) collectionsColumn.hidden = false;
		if (collectionsGrid) collectionsGrid.hidden = false;
		if (productGrid) productGrid.hidden = true;

		// Clear stale min-heights from Products view (especially empty-state sizing).
		productGrid?.style?.removeProperty('min-height');

		setToggleActive(VIEW_COLLECTIONS);
		syncSharedToolbar(VIEW_COLLECTIONS);

		const applyCollectionFiltersFromUrl = () => {
			document.dispatchEvent(
				new CustomEvent('collection-list:apply-url', {
					detail: {
						sectionId: getSectionId(),
						filterParams: filterParams.toString(),
					},
				})
			);
		};

		/* Defer until collections facets are visible in the layout. */
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				applyCollectionFiltersFromUrl();
				syncAllFilterUiFromParams(filterParams);
				scheduleToolbarAlignMode(VIEW_COLLECTIONS);
			});
		});

		syncAllPagePerRow();
		afterAllPageLayout();
		syncSearchFormViewInput(VIEW_COLLECTIONS);
	}

	function showAllPageProductsView(options = {}) {
		if (config && config.sectionId != null) {
			const sid = String(config.sectionId);
			const fresh = document.getElementById(`shopify-section-${sid}`);
			if (fresh) sectionRoot = fresh;
		}
		if (!sectionRoot) return;

		const leavingCollections = !window.__clistProductsViewActive;
		if (
			leavingCollections &&
			typeof window.__clistFlushCollectionsFiltersToUrl === 'function'
		) {
			window.__clistFlushCollectionsFiltersToUrl({ force: true });
		}

		const filterParams = dedupeFilterSearchParams(
			options.filterParams
				? new URLSearchParams(options.filterParams)
				: mergeFilterParamsForViewSwitch(VIEW_COLLECTIONS)
		);

		writeCanonicalFilterUrl(VIEW_PRODUCTS, filterParams);

		const productsFacets = sectionRoot.querySelector('[data-clist-products-facets]');
		const collectionsFacets = sectionRoot.querySelector('[data-clist-collections-facets]');
		const collectionsColumn = sectionRoot.querySelector('[data-clist-collections-column]');
		const collectionsGrid = sectionRoot.querySelector('[data-clist-collections-grid]');
		const productGrid = sectionRoot.querySelector('#ProductGridContainer');

		sectionRoot.classList.remove('clist-view-collections-active');
		sectionRoot.classList.add('clist-view-products-active');
		window.__clistProductsViewActive = true;
		if (productsFacets) productsFacets.hidden = false;
		if (collectionsFacets) {
			collectionsFacets.hidden = true;
		}
		if (collectionsColumn) collectionsColumn.hidden = true;
		if (collectionsGrid) collectionsGrid.hidden = true;
		if (productGrid) productGrid.hidden = false;

		// Ensure Products view recalculates empty-state sizing after toggles.
		requestAnimationFrame(() => {
			document.dispatchEvent(new CustomEvent('clist:sync-products-empty-state'));
		});

		setToggleActive(VIEW_PRODUCTS);
		syncSharedToolbar(VIEW_PRODUCTS);
		installAllPageHistoryPatch();

		if (isSearchPageMode()) {
			const urlParams = filterParams;
			const nativeParams = getFilterParamsFromNativeForm();
			const needsNativeRefresh =
				!filterParamsEqual(urlParams, nativeParams) || window.__clistSearchProductsGridStale;

			if (needsNativeRefresh) {
				window.__clistSearchProductsNativeRefetchActive = true;
				if (typeof window.__clistShowSearchProductsGridLoading === 'function') {
					window.__clistShowSearchProductsGridLoading();
				} else if (typeof window.__clistBeginSearchProductsLoading === 'function') {
					window.__clistBeginSearchProductsLoading({ force: true });
				}
				renderNativeProductsWithFilters(urlParams, { skipUrl: true });
				window.__clistSearchProductsGridStale = false;
			}

			syncAllPagePerRow();
			afterAllPageLayout();
			requestAnimationFrame(() => {
				syncAllFilterUiFromParams(filterParams);
				if (!needsNativeRefresh) {
					document.dispatchEvent(
						new CustomEvent('clist:search-products-shown', { detail: { sectionId: getSectionId() } })
					);
				}
			});
		} else {
			renderNativeProductsWithFilters(filterParams, { skipUrl: true });
			syncAllPagePerRow();
			afterAllPageLayout();
			requestAnimationFrame(() => {
				syncAllFilterUiFromParams(filterParams);
			});
		}
		syncSearchFormViewInput(VIEW_PRODUCTS);

		if (
			window.ClistProductsPlp &&
			typeof window.ClistProductsPlp.applyAuthoritativeCountFromHtml === 'function'
		) {
			window.ClistProductsPlp.applyAuthoritativeCountFromHtml();
		} else if (
			window.ClistProductsPlp &&
			typeof window.ClistProductsPlp.syncProductsToolbarCountFromDom === 'function'
		) {
			window.ClistProductsPlp.syncProductsToolbarCountFromDom();
		}
	}

	function wireAllPageToggle() {
		if (!sectionRoot) return;

		sectionRoot.addEventListener('click', (e) => {
			const btn = e.target.closest('[data-collections-products-toggle] [data-clist-view]');
			if (!btn || btn.disabled) return;
			const view = btn.getAttribute('data-clist-view');
			const productsVisible = isAllPageProductsVisible();

			if (view === VIEW_PRODUCTS && !productsVisible) {
				sectionRoot.querySelectorAll('[data-collections-products-toggle] [data-clist-view]').forEach((b) => {
					b.disabled = true;
				});
				Promise.resolve(showAllPageProductsView()).finally(() => {
					sectionRoot.querySelectorAll('[data-collections-products-toggle] [data-clist-view]').forEach((b) => {
						b.disabled = false;
					});
				});
			} else if (view === VIEW_COLLECTIONS && productsVisible) {
				showAllPageCollectionsView();
			}
		});
	}

	function onAllPagePopstate() {
		const view = getViewFromUrl();
		if (view === VIEW_COLLECTIONS) {
			showAllPageCollectionsView({ skipUrl: true });
		} else {
			showAllPageProductsView({ skipUrl: true });
		}
	}

	function syncSearchFormViewInput(view) {
		const form =
			sectionRoot?.querySelector('[data-clist-search-form]') ||
			document.querySelector('.template-search [data-clist-search-form]');
		if (!form) return;

		/* Fresh searches always default to Products; never carry view= in the query. */
		if (isSearchPageMode()) {
			form.querySelector('input[name="view"]')?.remove();
			return;
		}

		let input = form.querySelector('input[name="view"]');
		if (view === VIEW_COLLECTIONS) {
			if (!input) {
				input = document.createElement('input');
				input.type = 'hidden';
				input.name = 'view';
				form.appendChild(input);
			}
			input.value = VIEW_COLLECTIONS;
		} else if (input) {
			input.remove();
		}
	}

	function patchSearchProductsFacetRender() {
		if (!isSearchPageMode() || window.__searchProductsFacetRenderPatched) return;

		const tryPatch = () => {
			if (!window.FacetFiltersForm || typeof FacetFiltersForm.renderPage !== 'function') {
				return false;
			}

			window.__searchProductsFacetRenderPatched = true;
			const origRenderPage = FacetFiltersForm.renderPage.bind(FacetFiltersForm);

			FacetFiltersForm.renderPage = function patchedSearchProductsRenderPage(
				searchParams,
				event,
				updateURLHash = true
			) {
				if (
					!FacetFiltersForm.isSearchPageContext() ||
					!window.__clistProductsViewActive
				) {
					return origRenderPage(searchParams, event, updateURLHash);
				}

				const merged = appendSearchContextToParams(new URLSearchParams(searchParams || ''));
				if (!merged.has('type')) merged.set('type', 'product');
				merged.set('options[prefix]', 'any');

				const result = origRenderPage(merged.toString(), event, updateURLHash);
				requestAnimationFrame(() => {
					if (typeof window.__clistSyncAllFilterUiFromParams === 'function') {
						window.__clistSyncAllFilterUiFromParams(getCanonicalFilterParamsFromUrl());
					}
				});
				return result;
			};

			if (!FacetFiltersForm.__clistSearchUrlHashPatched) {
				const origUpdateURLHash = FacetFiltersForm.updateURLHash.bind(FacetFiltersForm);
				FacetFiltersForm.updateURLHash = function patchedSearchUpdateURLHash(searchParams) {
					if (FacetFiltersForm.isSearchPageContext() && window.__clistProductsViewActive) {
						const merged = appendSearchContextToParams(
							new URLSearchParams(searchParams || '')
						);
						if (!merged.has('type')) merged.set('type', 'product');
						merged.set('options[prefix]', 'any');
						const view = new URL(window.location.href).searchParams.get('view');
						if (view === VIEW_PRODUCTS) {
							merged.set('view', VIEW_PRODUCTS);
						}
						return origUpdateURLHash(merged.toString());
					}
					return origUpdateURLHash(searchParams);
				};
				FacetFiltersForm.__clistSearchUrlHashPatched = true;
			}

			return true;
		};

		if (!tryPatch()) {
			const poll = window.setInterval(() => {
				if (tryPatch()) window.clearInterval(poll);
			}, 50);
			window.setTimeout(() => window.clearInterval(poll), 5000);
		}
	}

	function wireSearchFormViewPersistence() {
		const form =
			sectionRoot?.querySelector('[data-clist-search-form]') ||
			document.querySelector('.template-search [data-clist-search-form]');
		if (!form || form.dataset.clistViewBound === '1') return;
		form.dataset.clistViewBound = '1';

		if (isSearchPageMode()) {
			form.addEventListener('submit', () => {
				form.querySelector('input[name="view"]')?.remove();
			});
			return;
		}

		form.addEventListener('submit', () => {
			const view = new URL(window.location.href).searchParams.get('view');
			if (view === VIEW_COLLECTIONS) {
				syncSearchFormViewInput(VIEW_COLLECTIONS);
			}
		});
	}

	function initAllPage(sectionConfig) {
		config = sectionConfig || {};
		const sid = getSectionId();
		sectionRoot = document.getElementById(`shopify-section-${sid}`);
		if (!sectionRoot) return;

		if (!sectionRoot.querySelector('[data-clist-collections-grid]')) return;

		wireAllPageToggle();
		bindProductsToolbarSort();
		wireSearchFormViewPersistence();
		patchSearchProductsFacetRender();
		if (window.ClistProductsPlp && typeof window.ClistProductsPlp.patchFacetFiltersForm === 'function') {
			window.ClistProductsPlp.patchFacetFiltersForm();
		}
		window.addEventListener('popstate', onAllPagePopstate);

		const initialView = getViewFromUrl();
		if (initialView === VIEW_COLLECTIONS) {
			showAllPageCollectionsView({ skipUrl: true });
		} else {
			showAllPageProductsView({ skipUrl: true });
		}
		syncSearchFormViewInput(initialView);
	}

	function init(sectionConfig) {
		config = sectionConfig || {};
		window.__clistProductsConfig = config;

		const sid = getSectionId();
		sectionRoot = document.getElementById(`shopify-section-${sid}`);
		if (!sectionRoot) return;

		collectionsContent = sectionRoot.querySelector('#view-collections-content');
		productsContent = sectionRoot.querySelector('#view-products-content');
		productsInner = sectionRoot.querySelector('[data-clist-products-inner]');
		toggleNav = sectionRoot.querySelector('[data-collections-products-toggle]');

		if (!collectionsContent || !productsContent || !toggleNav) return;

		const sortWrap = sectionRoot.querySelector('.facet-filters.facets-sorting');
		if (sortWrap) sortWrap.classList.add('clist-facets-collections-mode-sort');

		installHistoryPatch();
		wireToggle();
		bindProductsToolbarSort();

		window.addEventListener('popstate', onPopstate);

		document.addEventListener('clist:products-sort-changed', () => {
			if (!window.__clistProductsViewActive) return;
			htmlCache.delete(getActiveFilterParams().toString());
			showProductsView({ skipUrl: false, forceRefetch: true });
		});

		if (window.ClistProductsPlp && typeof window.ClistProductsPlp.patchFacetFiltersForm === 'function') {
			window.ClistProductsPlp.patchFacetFiltersForm();
		}

		const initialView = getViewFromUrl();
		if (initialView === VIEW_PRODUCTS) {
			activateProductsViewUi({ skipUrl: true });
			setProductsLoading(true);
			// Product fetch runs when collection-list-filters bootstrap completes
			// (applyFiltersFromUrl → __clistShowProductsView).
			setTimeout(() => {
				if (!productsInner?.querySelector('#ProductGridContainer') && window.__clistProductsViewActive) {
					showProductsView({ skipUrl: true });
				}
			}, 12000);
		} else {
			showCollectionsView({ skipUrl: true });
		}
	}

	window.__clistShowProductsView = showProductsView;

	function readConfigs() {
		const list = [];
		const nodes = document.querySelectorAll(
			'script[type="application/json"][id^="collection-list-facets-config-"]'
		);
		for (const node of nodes) {
			try {
				const data = JSON.parse(node.textContent.trim());
				if (data && (data.enableProductsToggle || data.enableCollectionsProductsToggle)) {
					list.push(data);
				}
			} catch (e) {
				/* ignore */
			}
		}
		return list;
	}

	function boot() {
		const configs = readConfigs();
		for (const cfg of configs) {
			if (cfg.enableCollectionsProductsToggle) {
				initAllPage(cfg);
			} else if (cfg.enableProductsToggle) {
				init(cfg);
			}
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', boot);
	} else {
		boot();
	}
})();

/**
 * Align VIEW+SORT wrapper left border with the last card column in the active grid below.
 * Width-only adjustment — no absolute positioning.
 */
(function () {
	const MOBILE_MAX = 749;
	const TABLET_TOOLBAR_MAX = 1239;
	const RESIZE_DEBOUNCE_MS = 100;
	const ROW_TOP_TOLERANCE_PX = 3;
	const MIN_CONTROLS_WIDTH_PX = 48;

	let resizeTimer = null;
	let pendingRaf = 0;

	function isToolbarVisible(toolbar) {
		if (!toolbar) return false;
		const style = window.getComputedStyle(toolbar);
		if (style.display === 'none' || style.visibility === 'hidden') return false;
		return toolbar.getClientRects().length > 0;
	}

	function isCardVisible(card) {
		if (!card || card.hidden) return false;
		if (
			card.classList.contains('hidden-by-pagination') ||
			card.classList.contains('hidden-by-filter')
		) {
			return false;
		}
		const style = window.getComputedStyle(card);
		if (style.display === 'none' || style.visibility === 'hidden') return false;
		const rect = card.getBoundingClientRect();
		return rect.width > 0 && rect.height > 0;
	}

	function getToolbarAlignMode(section) {
		const track = section.querySelector(
			'[data-clist-shared-toolbar] .clist-toolbar-track, [data-clist-shared-toolbar] .clist-toolbar-main'
		);
		return track?.getAttribute('data-clist-toolbar-align') === 'collections'
			? 'collections'
			: 'products';
	}

	function findVisibleProductGrid(section) {
		const selectors = [
			'#ProductGridContainer:not([hidden]) #product-grid',
			'.clist-view-products-panel:not([hidden]) #product-grid',
			'[data-clist-products-inner]:not([hidden]) #product-grid',
			'#ProductGridContainer #product-grid',
		];
		for (const selector of selectors) {
			const list = section.querySelector(selector);
			if (!list) continue;
			const host = list.closest('#ProductGridContainer, [data-clist-products-inner]');
			if (host && (host.hidden || window.getComputedStyle(host).display === 'none')) continue;
			return list;
		}
		return null;
	}

	function findVisibleCollectionsGrid(section) {
		const selectors = [
			'#CollectionListGridContainer:not([hidden]) [id^="CollectionListGrid"]',
			'[data-clist-collections-grid]:not([hidden]) [id^="CollectionListGrid"]',
			'#view-collections-content:not([hidden]) [id^="CollectionListGrid"]',
			'[id^="CollectionListGrid"]',
		];
		for (const selector of selectors) {
			const list = section.querySelector(selector);
			if (!list) continue;
			const host = list.closest(
				'#CollectionListGridContainer, [data-clist-collections-grid], #view-collections-content'
			);
			if (host && host.hidden) continue;
			if (host && window.getComputedStyle(host).display === 'none') continue;
			return list;
		}
		return null;
	}

	function findActiveCardGrid(section) {
		const mode = getToolbarAlignMode(section);
		if (mode === 'products') {
			return findVisibleProductGrid(section);
		}
		return findVisibleCollectionsGrid(section);
	}

	function getGridCards(gridList) {
		if (!gridList) return [];
		if (gridList.matches('[id^="CollectionListGrid"], .collections-grid')) {
			return [
				...gridList.querySelectorAll(
					':scope > li.collections-grid__card, :scope > li[data-collection-handle]'
				),
			];
		}
		return [...gridList.querySelectorAll(':scope > li.collection-grid-card')];
	}

	function getFirstRowLastVisibleCard(cards) {
		const visible = cards.filter(isCardVisible);
		if (!visible.length) return null;

		const firstTop = visible[0].getBoundingClientRect().top;
		const rowCards = visible.filter((card) => {
			return (
				Math.abs(card.getBoundingClientRect().top - firstTop) <= ROW_TOP_TOLERANCE_PX
			);
		});
		return rowCards[rowCards.length - 1] || null;
	}

	function clearControlsWidth(controls) {
		if (!controls) return;
		controls.style.removeProperty('width');
		controls.style.removeProperty('flex');
	}

	function alignToolbarControlsForSection(section) {
		const toolbar = section.querySelector('[data-clist-shared-toolbar]');
		if (!toolbar) return;

		toolbar.classList.add('is-clist-toolbar-js-align');

		const controls = toolbar.querySelector('.clist-toolbar-controls');
		const track = toolbar.querySelector('.clist-toolbar-track, .clist-toolbar-main');
		if (!controls || !track) return;

		if (window.innerWidth <= MOBILE_MAX || !isToolbarVisible(toolbar)) {
			clearControlsWidth(controls);
			return;
		}

		/* 750–1239px: toolbar track uses CSS grid + display:contents (collections-products-toggle.css) */
		if (window.innerWidth <= TABLET_TOOLBAR_MAX) {
			clearControlsWidth(controls);
			return;
		}

		const gridList = findActiveCardGrid(section);
		if (!gridList) {
			clearControlsWidth(controls);
			return;
		}

		const lastCard = getFirstRowLastVisibleCard(getGridCards(gridList));
		if (!lastCard) {
			clearControlsWidth(controls);
			return;
		}

		const trackRect = track.getBoundingClientRect();
		const lastCardLeft = lastCard.getBoundingClientRect().left;
		const widthPx = Math.round(trackRect.right - lastCardLeft);

		if (!Number.isFinite(widthPx) || widthPx < MIN_CONTROLS_WIDTH_PX) {
			clearControlsWidth(controls);
			return;
		}

		controls.style.width = `${widthPx}px`;
		controls.style.flex = '0 0 auto';
	}

	function alignToolbarControls() {
		document.querySelectorAll('[id^="shopify-section-"]').forEach((section) => {
			if (!section.querySelector('[data-clist-shared-toolbar]')) return;
			alignToolbarControlsForSection(section);
		});
	}

	function scheduleAlignToolbarControls() {
		if (pendingRaf) cancelAnimationFrame(pendingRaf);
		pendingRaf = requestAnimationFrame(() => {
			pendingRaf = requestAnimationFrame(() => {
				pendingRaf = 0;
				alignToolbarControls();
			});
		});
	}

	function onResizeDebounced() {
		if (resizeTimer) clearTimeout(resizeTimer);
		resizeTimer = setTimeout(scheduleAlignToolbarControls, RESIZE_DEBOUNCE_MS);
	}

	function initToolbarAlign() {
		document.querySelectorAll('[data-clist-shared-toolbar]').forEach((toolbar) => {
			toolbar.classList.add('is-clist-toolbar-js-align');
		});
		scheduleAlignToolbarControls();
	}

	window.ClistToolbarAlign = {
		align: alignToolbarControls,
		schedule: scheduleAlignToolbarControls,
	};

	window.addEventListener('resize', onResizeDebounced);

	document.addEventListener('clist:products-injected', scheduleAlignToolbarControls);
	document.addEventListener('clist:products-view-ready', scheduleAlignToolbarControls);
	document.addEventListener('clist:collections-per-row-changed', scheduleAlignToolbarControls);
	document.addEventListener('search:load-more-complete', scheduleAlignToolbarControls);
	document.addEventListener('clist:sync-search-column-height', scheduleAlignToolbarControls);
	document.addEventListener('shopify:section:load', scheduleAlignToolbarControls);

	document.addEventListener('change', (event) => {
		if (
			event.target.closest(
				'facet-filters-form, .clist-facet-checkbox, [data-clist-products-sort], [data-clist-collections-sort]'
			)
		) {
			scheduleAlignToolbarControls();
		}
	});

	document.addEventListener(
		'click',
		(event) => {
			if (event.target.closest('.per-row__button, [data-clist-view], .js-load-more')) {
				scheduleAlignToolbarControls();
			}
		},
		true
	);

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initToolbarAlign);
	} else {
		initToolbarAlign();
	}
})();
